import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";

import {
  GET_ALL_MESSAGES_OF_ORDER,
  SEND_MESSAGE,
  MARK_NOTIFICATIONS_AS_READ,
  MARK_CHAT_MESSAGES_AS_READ,
} from "/@/gql";
import {
  FileType,
  OrderViewPage,
  useFiles,
  UserRole,
  getValideFiles,
  UserType,
  GET_ORDER_BY_UNIQUE_ATTRIBUTE,
  joinSocketRoom,
  onSocketEvent,
  GET_FILES_DOWNLOAD_URLS,
  Message,
} from "@jasper/shared";
import type { SearchState } from "../../utils/searchStatePersistence";
import { shouldLeaveOrder } from "../../utils/orderUtils";
import { UseAuth } from "/@/contexts";
import { useEffect, useState } from "react";
import { MARK_FILE_AS_VIEWED } from "../../gql/file";
import { AssignNewProviderEvent } from "../home/types";
import { notifications } from "@mantine/notifications";
import { useOrderListFilter } from "../home/contexts/OrderListFilterContext";
const __TAKE__ = 5;

const OrderView = () => {
  const { order_id } = useParams();
  const { user } = UseAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as {
    searchState?: SearchState | null;
    from?: string;
  } | null;
  const originatingSearchState = locationState?.searchState ?? null;
  const originatingFrom = locationState?.from;
  const isOpenedFromChatList = originatingFrom === "CHAT_LIST";
  const { setFilters, setPage: setFilterPage } = useOrderListFilter();

  if (!order_id) {
    return;
  }

  const { data, refetch } = useQuery(GET_ORDER_BY_UNIQUE_ATTRIBUTE, {
    variables: {
      where: {
        id: order_id,
      },
    },
    fetchPolicy: "cache-first",
  });

  const { getFileUrl, uploadFileToS3 } = useFiles(order_id);

  const [message, setMessage] = useState("");
  const [scrollBottom, setScrollBottom] = useState(true);
  const [filesToSend, setFilesToSend] = useState<File[]>([]);
  const [page, setPage] = useState(0);
  const [messagesToShow, setMessageToShow] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState<Message>();
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [pictureToMarkAsRead, setPictureToMarkAsRead] = useState<string | null>(
    null
  );
  const [providerGroupId, setProviderGroupId] = useState<string | null>(null);

  const getFilesWithPath = async (messages: Message[]): Promise<File[]> => {
    return await Promise.all(
      messages.map(async msg => {
        const updatedFiles = await Promise.all(
          (msg.files ?? []).map(async file => {
            try {
              return {
                id: file.id,
                path: await getFileUrl(file.key),
                pathCompressed: await getFileUrl(
                  file.keyCompressed ?? file.key
                ),
                key: file.key,
                keyCompressed: file.keyCompressed,
                fileType: file.fileType,
                read: file.read,
              };
            } catch (error) {
              console.error(
                `Error fetching path for file key: ${file.key}`,
                error
              );
              return { key: file.key, fileType: file.fileType, path: null };
            }
          })
        );
        return { ...msg, files: updatedFiles };
      })
    );
  };

  const { data: dataChats, loading } = useQuery(GET_ALL_MESSAGES_OF_ORDER, {
    variables: {
      take: __TAKE__,
      skip: __TAKE__ * page,
      where: {
        orderId: {
          equals: order_id,
        },
      },
    },
    fetchPolicy: "cache-and-network",
    onCompleted: data => {
      if (data?.getAllChatsOfOrder?.count) {
        const newMessages = data?.getAllChatsOfOrder.notification ?? [];
        getFilesWithPath(newMessages).then(processedMessages => {
          if (processedMessages.length === newMessages.length) {
            setMessageToShow(prev => [
              ...processedMessages.toReversed(),
              ...prev,
            ]);
            setScrollBottom(false);

            if (
              [...processedMessages, ...messagesToShow].length ===
              data?.getAllChatsOfOrder.count
            ) {
              setHasMore(false);
            } else {
              setHasMore(true);
            }
          }
        });
      } else {
        setHasMore(false);
      }
    },
  });

  const fetchMoreMessages = () => {
    if (hasMore) {
      setPage(page + 1);
    }
  };
  const [send] = useMutation(SEND_MESSAGE);
  const [updateMessages] = useMutation(MARK_NOTIFICATIONS_AS_READ);
  const [markFileAsRead] = useMutation(MARK_FILE_AS_VIEWED);
  const [markChatMessagesAsRead] = useMutation(MARK_CHAT_MESSAGES_AS_READ);
  const setChatMessagesAsRead = async (providerGroupId: string) => {
    await markChatMessagesAsRead({
      variables: {
        supplier: providerGroupId,
        data: {
          read: {
            set: true,
          },
        },
        where: {
          orderId: {
            equals: order_id,
          },
          userGroupId: {
            not: {
              equals: user?.userGroupId,
            },
          },
        },
      },
    });
  };

  const setChatOrderAsRead = async (providerGroupId: string) => {
    await updateMessages({
      variables: {
        supplier: providerGroupId,
        data: {
          read: {
            set: true,
          },
        },
        where: {
          orderId: {
            equals: order_id,
          },
          userGroupId: {
            not: {
              equals: user?.userGroupId,
            },
          },
        },
      },
    });
  };

  const sendMessage = async () => {
    if (!data?.getOrderByUniqueAttribute?.provider?.id) {
      notifications.show({
        title: "You must assign a provider first before sending a message",
        color: "red",
        message: "",
      });
      return;
    }
    if (filesToSend.length !== 0) {
      uploadFileToS3(filesToSend, FileType.CHAT_FILE).then(async dataUpload => {
        if (data && data.length !== 0) {
          await send({
            variables: {
              args: {
                order: {
                  connect: {
                    id: order_id,
                  },
                },
                userGroup: {
                  connect: {
                    id: user.userGroupId,
                  },
                },
                message: message,
                files: {
                  create: dataUpload,
                },
              },
              olderChatRead:
                messagesToShow.filter(
                  (msg: Message) =>
                    msg.read === false && msg.userGroup.id === user.userGroupId
                ).length === 0,
              supplier: data?.getOrderByUniqueAttribute?.provider?.id,
            },
          });
        }
        setFilesToSend([]);
      });
    } else {
      await send({
        variables: {
          args: {
            order: {
              connect: {
                id: order_id,
              },
            },
            userGroup: {
              connect: {
                id: user.userGroupId,
              },
            },
            message: message,
          },
          olderChatRead:
            messagesToShow.filter(
              (msg: Message) =>
                msg.read === false && msg.userGroup.id === user.userGroupId
            ).length === 0,
          supplier: data?.getOrderByUniqueAttribute?.provider?.id,
        },
      });
    }
  };

  const markFileAsReadHandler = () => {
    markFileAsRead({
      variables: {
        id: pictureToMarkAsRead,
      },
    }).then(() => {
      refetch();
    });
  };

  const [orderComments, setOrderComments] = useState<Message[]>([]);

  const [fileWithPath, setFileWithPath] = useState<File[]>([]);
  const [getFilesDownloadUrls] = useLazyQuery(GET_FILES_DOWNLOAD_URLS, {
    onCompleted: value => {
      setFileWithPath(value?.getSignedDownloadUrlsFromKeys?.files || []);
    },
  });

  useEffect(() => {
    if (
      data?.getOrderByUniqueAttribute?.provider?.id &&
      data.getOrderByUniqueAttribute.provider.id !== providerGroupId
    ) {
      setProviderGroupId(data?.getOrderByUniqueAttribute.provider.id);
    }
    if (data?.getOrderByUniqueAttribute) {
      getFilesWithPath(data.getOrderByUniqueAttribute.orderComment).then(
        orderCommentsWithUpdatedFilesWithFilePath => {
          setOrderComments(orderCommentsWithUpdatedFilesWithFilePath);
        }
      );
    }
    if (
      data &&
      user.role === UserRole.USER &&
      data?.getOrderByUniqueAttribute?.provider?.id !== user.userGroupId
    ) {
      navigate("/chats?page=0");
    }
    if (data?.getOrderByUniqueAttribute?.files.length > 0) {
      const validFiles = getValideFiles(
        data?.getOrderByUniqueAttribute?.files ?? []
      );
      getFilesDownloadUrls({ variables: { args: { files: [...validFiles] } } });
    }
  }, [data?.getOrderByUniqueAttribute]);

  useEffect(() => {
    if (
      isOpenedFromChatList &&
      data?.getOrderByUniqueAttribute !== undefined &&
      dataChats?.getAllChatsOfOrder?.notification?.filter(
        (msg: Message) =>
          msg.read === false && msg.userGroup.id !== user.userGroupId
      ).length > 0
    ) {
      setChatMessagesAsRead(data?.getOrderByUniqueAttribute.provider?.id);
    }
  }, [
    data?.getOrderByUniqueAttribute,
    dataChats?.getAllChatsOfOrder?.notification,
    user.userGroupId,
  ]);

  useEffect(() => joinSocketRoom("chat-" + order_id), [order_id]);

  useEffect(() => {
    if (!providerGroupId) {
      return;
    }

    if (
      isOpenedFromChatList &&
      data?.getOrderByUniqueAttribute?.ChatMarkAsUnread?.length > 0
    ) {
      setChatOrderAsRead(providerGroupId);
    }

    const markDisplayedMessagesAsRead = () =>
      setMessageToShow((prevMessages: Message[]) =>
        prevMessages.map((msg: Message) => ({ ...msg, read: true }))
      );

    const isSupplier = user.type === UserType.SUPPLIER;

    const unsubscribes = [
      onSocketEvent<Message>("chat", newMessage => {
        if (newMessage.files.length > 0) {
          getFilesWithPath([newMessage]).then(msg => {
            setNewMessage({ ...msg[0], read: true });
            setScrollBottom(true);
          });
        } else {
          newMessage.files = [];
          setNewMessage(newMessage);
          setScrollBottom(true);
        }
      }),
      onSocketEvent(
        isSupplier
          ? "setAdminMsgInProviderViewAsRead"
          : "setProviderMsgInAdminViewAsRead",
        markDisplayedMessagesAsRead
      ),
    ];

    // Sent to the provider-pastilles-<group> room, joined by the navbar
    // (useNavbarNotifications), as in useSocketEvents
    if (isSupplier) {
      unsubscribes.push(
        onSocketEvent<AssignNewProviderEvent>("assignNewProvider", args => {
          if (shouldLeaveOrder(args.order, user.userGroupId, order_id)) {
            navigate("/chats?page=0");
          }
        })
      );
    }

    return () => unsubscribes.forEach(unsubscribe => unsubscribe());
  }, [providerGroupId]);

  useEffect(() => {
    if (newMessage) {
      setMessageToShow(previous =>
        previous.some(message => message.id === newMessage.id)
          ? previous
          : [...previous, newMessage]
      );
    }
  }, [newMessage]);

  useEffect(() => {
    if (pictureToMarkAsRead !== null) {
      markFileAsReadHandler();
    }
  }, [pictureToMarkAsRead]);

  const handleSearch = (searchText: string) => {
    navigate("/home");
    setFilters({ search: searchText });
    setFilterPage(0); // Reset to first page when searching
  };

  const ORIGINS_USING_HISTORY_BACK = new Set([
    "CHAT_LIST",
    "HELP_LIST",
    "HOME",
  ]);

  const handleBack = () => {
    if (ORIGINS_USING_HISTORY_BACK.has(originatingFrom ?? "")) {
      navigate(-1);
    } else {
      navigate("/home", {
        state: {
          fromOrderView: true,
          searchState: originatingSearchState,
        },
      });
    }
  };

  return (
    <OrderViewPage
      orderComments={orderComments}
      fileWithPath={fileWithPath ?? []}
      order={data?.getOrderByUniqueAttribute}
      user={user}
      refetch={refetch}
      setMessage={setMessage}
      messages={messagesToShow}
      sendMessage={sendMessage}
      filesToSend={filesToSend}
      setFilesToSend={setFilesToSend}
      loading={loading}
      hasMore={hasMore}
      fetchMoreMessages={fetchMoreMessages}
      scrollBottom={scrollBottom}
      setPictureToMarkAsRead={setPictureToMarkAsRead}
      onSearch={handleSearch}
      onBack={handleBack}
    />
  );
};

export default OrderView;
